import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Plus, Minus } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

const featured = [
  { num: "01", title: "Dossier 137", cat: "Affiche cinéma — Festival de Cannes", catEn: "Film poster — Cannes Film Festival", year: "2025", slug: "/projet/dossier-137" },
  { num: "06", title: "Workshop Mumbaï", cat: "Affiche typographique", catEn: "Typographic poster", year: "2026", slug: "/projet/workshop-mumbai" },
  { num: "08", title: "Fisheye", cat: "Magazine — éditorial", catEn: "Magazine — editorial", year: "2025", slug: "/projet/fisheye" },
  { num: "09", title: "Aethera", cat: "Illustration & identité", catEn: "Illustration & identity", year: "2026", slug: "/projet/aethera" },
];

const more = [
  { num: "07", title: "Synrgy", cat: "DA & animation 3D", catEn: "Art direction & 3D animation", year: "2026", slug: "/projet/synrgy" },
  { num: "02", title: "Phoenix", cat: "Pochette vinyle", catEn: "Vinyl sleeve", year: "2025", slug: "/projet/phoenix" },
  { num: "03", title: "Let's Fall in Love", cat: "Éditorial & print", catEn: "Editorial & print", year: "2025", slug: "/projet/lets-fall-in-love" },
  { num: "05", title: "Oscar Niemeyer", cat: "Design typographique", catEn: "Typographic design", year: "2026", slug: "/projet/oscar-niemeyer" },
];

function ProjectRow({ p, flash, onHover, onLeave, onClick, cat, delay = 0 }) {
  return (
    <FadeUp delay={delay}>
      <button
        id={`projet-${p.num}`}
        data-testid={`project-row-${p.num}`}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        onClick={onClick}
        className="group w-full flex items-center gap-6 md:gap-10 py-8 md:py-10 border-b border-[#1A1A1A] text-left cursor-pointer"
      >
        <span className={`font-mono-tech text-xs transition-colors duration-300 ${flash === p.num ? "text-[#75FB4E]" : "text-[#808080] group-hover:text-[#75FB4E]"}`}>
          /{p.num}
        </span>
        <span className={`font-display text-3xl md:text-6xl uppercase leading-none transition-all duration-500 ease-out ${flash === p.num ? "text-[#75FB4E] translate-x-4" : "text-[#E5E5E5] group-hover:text-[#75FB4E] group-hover:translate-x-4"}`}>
          {p.title}
        </span>
        <span className="ml-auto hidden sm:block font-mono-tech text-[10px] md:text-xs tracking-widest text-[#808080] uppercase">
          {cat}
        </span>
        <span className="hidden md:block font-mono-tech text-xs text-[#808080]">{p.year}</span>
        <ArrowUpRight className="w-6 h-6 text-[#808080] group-hover:text-[#75FB4E] group-hover:rotate-45 transition-all duration-300" />
      </button>
    </FadeUp>
  );
}

export default function Projects() {
  const { t, lang } = useLang();
  const [hovering, setHovering] = useState(false);
  const [flash, setFlash] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onFlash = (e) => {
      setFlash(e.detail);
      setTimeout(() => setFlash(null), 1800);
    };
    const onReveal = (e) => {
      if (more.some((p) => p.num === e.detail)) setOpen(true);
    };
    window.addEventListener("flash-project", onFlash);
    window.addEventListener("reveal-project", onReveal);
    return () => {
      window.removeEventListener("flash-project", onFlash);
      window.removeEventListener("reveal-project", onReveal);
    };
  }, []);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 260, damping: 26 });
  const sy = useSpring(y, { stiffness: 260, damping: 26 });

  const onMove = (e) => {
    x.set(e.clientX);
    y.set(e.clientY);
  };

  const rowProps = (p) => ({
    p,
    flash,
    cat: lang === "en" ? p.catEn : p.cat,
    onHover: () => setHovering(true),
    onLeave: () => setHovering(false),
    onClick: () => navigate(p.slug),
  });

  return (
    <section id="projets" data-testid="projects-section" onMouseMove={onMove} className="relative px-6 md:px-12 py-28 md:py-40">
      <FadeUp className="flex items-baseline gap-4 mb-16 md:mb-24">
        <span className="font-mono-tech text-sm text-[#75FB4E]">(02)</span>
        <h2 className="font-display text-5xl md:text-8xl uppercase leading-none">{t("projects.section")}</h2>
        <span className="font-mono-tech text-xs text-[#808080] ml-auto hidden md:inline">{t("projects.selection")}</span>
      </FadeUp>

      <div className="border-t border-[#1A1A1A]">
        {featured.map((p, i) => (
          <ProjectRow key={p.num} {...rowProps(p)} delay={i * 0.05} />
        ))}

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              data-testid="projects-more"
              className="overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              {more.map((p) => (
                <ProjectRow key={p.num} {...rowProps(p)} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <button
          data-testid="projects-toggle"
          onClick={() => setOpen((v) => !v)}
          className="group w-full flex items-center justify-between py-6 md:py-8 border-b border-[#1A1A1A] font-mono-tech text-xs tracking-[0.3em] uppercase text-[#808080] hover:text-[#75FB4E] transition-colors duration-300"
        >
          <span className="inline-flex items-center gap-4">
            <span className="w-8 h-8 border border-current flex items-center justify-center rounded-full group-hover:rotate-90 transition-transform duration-500">
              {open ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            </span>
            {open ? t("projects.reduce") : `${t("projects.viewOthers")} (+${more.length})`}
          </span>
          <span className="hidden sm:inline">{String(featured.length + (open ? more.length : 0)).padStart(2, "0")} / {String(featured.length + more.length).padStart(2, "0")}</span>
        </button>
      </div>

      <AnimatePresence>
        {hovering && (
          <motion.div
            data-testid="project-hover-label"
            className="fixed top-0 left-0 z-[60] pointer-events-none hidden md:block"
            style={{ x: sx, y: sy }}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="block ml-5 mt-5 whitespace-nowrap bg-[#75FB4E] text-[#050505] font-mono-tech text-[10px] tracking-[0.3em] uppercase px-4 py-2 rounded-full">
              {t("projects.hoverLabel")}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
